import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtMoney } from "../../lib/api";
import { serviceName } from "../../lib/constants";
import { StatCard } from "../../components/shared/StatCard";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import {
  PackageOpen, CalendarCheck2, CalendarClock, Users, FileClock, Headset,
  Loader2, MapPin, Phone, UserPlus, ArrowRight, ShieldAlert, History,
} from "lucide-react";

const payTone = {
  paid: "text-emerald-500 border-emerald-500/30 bg-emerald-500/10",
  deposit: "text-amber-500 border-amber-500/30 bg-amber-500/10",
  unpaid: "text-muted-foreground border-border bg-secondary/60",
};

function PaymentPill({ status, deposit }) {
  const label = status === "paid" ? "Paid" : deposit ? "Deposit" : "Unpaid";
  const tone = status === "paid" ? payTone.paid : deposit ? payTone.deposit : payTone.unpaid;
  return (
    <span className={`font-mono-cc text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded-full border ${tone}`}>
      {label}
    </span>
  );
}

function JobCard({ job, onAssign }) {
  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid={`dispatch-job-${job.mission_number}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono-cc text-[11px] text-[var(--cc-accent)]">{job.mission_number}</p>
          <p className="font-heading font-semibold truncate">{job.customer_name}</p>
          <p className="text-xs text-muted-foreground">{serviceName(job.service_type)}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <StatusBadge status={job.status} />
          <PaymentPill status={job.payment_status} deposit={job.deposit_paid} />
        </div>
      </div>

      <div className="space-y-1 text-xs text-muted-foreground">
        <p className="flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5 shrink-0" />
          {job.scheduled_date || "Date TBD"} · {job.scheduled_time || "—"}</p>
        {job.pickup_address && (
          <p className="flex items-center gap-1.5 truncate"><MapPin className="w-3.5 h-3.5 shrink-0" />
            {job.pickup_address}{job.dropoff_address ? ` → ${job.dropoff_address}` : ""}</p>
        )}
        {job.customer_phone && (
          <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 shrink-0" />{job.customer_phone}</p>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="text-xs">
          <span className="font-heading font-bold">{fmtMoney(job.value)}</span>
          {job.payout_per_helper ? (
            <span className="text-muted-foreground"> · payout {fmtMoney(job.payout_per_helper)}/crew</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {job.crew?.length ? (
            <span className="text-[11px] text-muted-foreground truncate max-w-[120px]">{job.crew.join(", ")}</span>
          ) : (
            <span className="text-[11px] text-amber-500 font-semibold">Unassigned</span>
          )}
          <button data-testid={`dispatch-assign-${job.mission_number}`} onClick={() => onAssign(job)}
            className="flex items-center gap-1 rounded-full border border-[var(--cc-accent)]/40 bg-[var(--cc-accent)]/10 text-[var(--cc-accent)] text-xs font-semibold px-3 py-1.5 hover:bg-[var(--cc-accent)]/20 transition-colors">
            <UserPlus className="w-3.5 h-3.5" /> {job.crew?.length ? "Reassign" : "Assign"}
          </button>
        </div>
      </div>
      {job.notes ? <p className="text-[11px] text-muted-foreground border-t pt-2 leading-relaxed">{job.notes}</p> : null}
    </div>
  );
}

export default function DispatchDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);

  const load = () => {
    api.get("/ops/dispatch-summary").then((r) => setData(r.data)).catch(() => {});
    api.get("/ops/dispatch-logs?limit=15").then((r) => setLogs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const goAssign = (job) => navigate(`/ops/dispatch?mission=${job.id}`);

  if (!data)
    return (
      <div className="flex items-center justify-center gap-3 py-24 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading dispatch board…
      </div>
    );

  const c = data.counts;

  return (
    <div className="space-y-6" data-testid="dispatch-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline-cc">Dispatch Command</p>
          <h1 className="page-title mt-1.5">
            {user?.dispatcher_kind === "bot" ? "Grok Bot — Dispatch Deck" : "Dispatch Deck"}
          </h1>
        </div>
        <span className="font-mono-cc text-[11px] tracking-[0.14em] uppercase text-muted-foreground" data-testid="dispatch-date">
          {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
        </span>
      </div>

      {c.quotes_pending_approval > 0 && (
        <Link to="/ops/quotes" data-testid="dispatch-approval-banner"
          className="flex items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span className="flex-1">{c.quotes_pending_approval} quote{c.quotes_pending_approval !== 1 ? "s" : ""} awaiting owner approval (above dispatcher limit).</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}

      {/* Operational widgets — no revenue / profit */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard testId="dispatch-kpi-unassigned" label="Unassigned Jobs" value={c.unassigned} icon={PackageOpen} accent to="/ops/dispatch" />
        <StatCard testId="dispatch-kpi-today" label="Today's Jobs" value={c.todays_jobs} icon={CalendarClock} to="/ops/calendar" />
        <StatCard testId="dispatch-kpi-booked" label="Today Booked" value={c.todays_booked} icon={CalendarCheck2} to="/ops/missions" />
        <StatCard testId="dispatch-kpi-crews" label="Crews Available" value={`${c.crews_available}/${c.crews_total}`} icon={Users} to="/ops/resources" />
        <StatCard testId="dispatch-kpi-quotes" label="Quotes Waiting" value={c.quotes_waiting} icon={FileClock} to="/ops/quotes" />
        <StatCard testId="dispatch-kpi-support" label="Support Unread" value={c.support_unread} icon={Headset} to="/ops/support" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Unassigned jobs */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold tracking-tight flex items-center gap-2">
              <PackageOpen className="w-4 h-4 text-[var(--cc-accent)]" /> Unassigned Jobs
            </h2>
            <Link to="/ops/dispatch" className="text-xs text-[var(--cc-accent)] font-semibold">Open dispatch board →</Link>
          </div>
          {data.unassigned_jobs.length === 0 ? (
            <div className="rounded-xl border bg-card p-8 text-center text-sm text-muted-foreground">
              All active jobs have a crew assigned. 🎯
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {data.unassigned_jobs.map((j) => <JobCard key={j.id} job={j} onAssign={goAssign} />)}
            </div>
          )}
        </div>

        {/* Side rail: today + quotes */}
        <div className="space-y-6">
          <div className="rounded-xl border bg-card">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 className="font-heading font-bold tracking-tight text-sm">Today's Schedule</h2>
              <Link to="/ops/calendar" className="text-xs text-[var(--cc-accent)] font-semibold">Calendar →</Link>
            </div>
            <div className="divide-y max-h-80 overflow-y-auto">
              {data.todays_jobs.length === 0 && <p className="p-5 text-sm text-muted-foreground">Nothing scheduled today.</p>}
              {data.todays_jobs.map((m) => (
                <div key={m.id} className="p-4" data-testid={`dispatch-today-${m.mission_number}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-mono-cc text-xs text-[var(--cc-accent)]">{m.mission_number} · {m.scheduled_time || "—"}</p>
                    <StatusBadge status={m.status} />
                  </div>
                  <p className="text-sm font-semibold mt-1">{m.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{serviceName(m.service_type)} · {m.crew?.length ? m.crew.join(", ") : "no crew"}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border bg-card">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 className="font-heading font-bold tracking-tight text-sm">Quotes Waiting</h2>
              <Link to="/ops/quotes" className="text-xs text-[var(--cc-accent)] font-semibold">Inbox →</Link>
            </div>
            <div className="divide-y max-h-72 overflow-y-auto">
              {data.quotes_waiting.length === 0 && <p className="p-5 text-sm text-muted-foreground">No quotes waiting.</p>}
              {data.quotes_waiting.map((q) => (
                <Link key={q.id} to="/ops/quotes" className="block p-4 hover:bg-secondary/40 transition-colors" data-testid={`dispatch-quote-${q.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold truncate">{q.name}</p>
                    {q.estimated_total ? <span className="font-heading font-bold text-sm">{fmtMoney(q.estimated_total)}</span> : null}
                  </div>
                  <p className="text-xs text-muted-foreground">{serviceName(q.service_type)}</p>
                </Link>
              ))}
            </div>
          </div>
          <div className="rounded-xl border bg-card">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 className="font-heading font-bold tracking-tight text-sm flex items-center gap-2">
                <History className="w-4 h-4 text-[var(--cc-accent)]" /> Dispatch Activity
              </h2>
            </div>
            <div className="divide-y max-h-72 overflow-y-auto">
              {logs.length === 0 && <p className="p-5 text-sm text-muted-foreground">No dispatch activity yet.</p>}
              {logs.map((l) => (
                <div key={l.id} className="p-4 flex items-start gap-3" data-testid={`dispatch-log-${l.id}`}>
                  <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${l.action === "reassign" ? "bg-amber-500" : l.action === "assign" ? "bg-emerald-500" : "bg-[var(--cc-accent)]"}`} />
                  <div className="min-w-0">
                    <p className="text-xs">
                      <span className="font-semibold">{l.mission_number}</span>{" "}
                      <span className="text-muted-foreground">{l.detail}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground font-mono-cc uppercase tracking-wider mt-0.5">
                      {l.actor_name}{l.actor_kind === "bot" ? " · Bot" : l.actor_kind === "owner" ? " · Owner" : ""} · {new Date(l.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
